import React, { useState, useRef } from 'react';
import { 
  Box, 
  IconButton, 
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { 
  Mic as MicIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { responses } from '../../services/api';
import { theme } from '../../theme';

export interface AudioRecorderCoreProps {
  onRecordingComplete: (blob: Blob | null, transcription?: string) => void;
  onTranscriptionStart?: () => void;
  onTranscriptionComplete?: (transcription: string) => void;
  onError?: (error: string) => void;
  isSubmitting?: boolean;
}

export const AudioRecorderCore: React.FC<AudioRecorderCoreProps> = ({
  onRecordingComplete,
  onTranscriptionStart,
  onTranscriptionComplete,
  onError,
  isSubmitting = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showTranscriptionDialog, setShowTranscriptionDialog] = useState(false);
  const [transcription, setTranscription] = useState<string>('');
  const [editedTranscription, setEditedTranscription] = useState<string>('');
  const [confirmedTranscription, setConfirmedTranscription] = useState<string>('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordingBlob(blob);
        transcribeAudio(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.onerror = (event) => {
        onError?.('Recording error occurred. Please try again.');
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      onError?.('Could not access microphone. Please ensure you have granted permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const deleteRecording = () => {
    console.log('[AudioRecorderCore] Deleting recording');
    
    setRecordingBlob(null);
    setTranscription('');
    setEditedTranscription('');
    setConfirmedTranscription('');
    onRecordingComplete(null, '');
  };

  const transcribeAudio = async (blob: Blob) => {
    console.log('[AudioRecorderCore] Starting transcription:', {
      blobSize: blob.size
    });
    
    setIsTranscribing(true);
    onTranscriptionStart?.();
    
    try {
      const response = await responses.transcribeAudio(blob);
      
      if (!response.transcription) {
        throw new Error('No transcription received');
      }

      console.log('[AudioRecorderCore] Transcription received:', {
        transcriptionLength: response.transcription.length
      });

      const transcription = response.transcription;
      setTranscription(transcription);
      setEditedTranscription(transcription);
      setShowTranscriptionDialog(true);
      onTranscriptionComplete?.(transcription);
    } catch (error) {
      console.error('[AudioRecorderCore] Transcription error:', error);
      onError?.('Failed to transcribe audio. Please try again or type your response.');
      setRecordingBlob(null);
      onRecordingComplete(null, '');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleConfirmTranscription = () => {
    console.log('[AudioRecorderCore] Confirming transcription:', {
      transcription: editedTranscription,
      hasBlob: !!recordingBlob,
      blobSize: recordingBlob?.size
    });
    
    setConfirmedTranscription(editedTranscription);
    onRecordingComplete(recordingBlob, editedTranscription);
    setShowTranscriptionDialog(false);
  };

  const handleReRecordClick = (event: React.MouseEvent) => {
    // Stop event propagation to prevent any parent handlers from firing
    event.preventDefault();
    event.stopPropagation();
    
    console.log('[AudioRecorderCore] Re-record button clicked directly');
    
    // Close dialog first
    setShowTranscriptionDialog(false);
    
    // Use requestAnimationFrame to ensure the dialog close is processed
    // before we reset the state
    window.requestAnimationFrame(() => {
      // Clear all recording data
      setRecordingBlob(null);
      setTranscription('');
      setEditedTranscription('');
      setConfirmedTranscription('');
      
      // Notify parent component
      onRecordingComplete(null, '');
      
      console.log('[AudioRecorderCore] Recording data cleared');
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {!isRecording && !recordingBlob && (
            <IconButton
              onClick={startRecording}
              disabled={isSubmitting}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'rgba(144, 202, 249, 0.08)'
                }
              }}
            >
              <MicIcon />
            </IconButton>
          )}

          {isRecording && (
            <IconButton
              onClick={stopRecording}
              disabled={isSubmitting}
              sx={{
                color: theme.palette.error.main,
                animation: 'pulse 2s infinite'
              }}
            >
              <StopIcon />
            </IconButton>
          )}

          {recordingBlob && !isRecording && (
            <>
              <IconButton
                onClick={deleteRecording}
                disabled={isSubmitting}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)'
                  }
                }}
                title="Delete recording"
              >
                <DeleteIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  // Clear recording and start a new one
                  deleteRecording();
                  // Use setTimeout to ensure state is updated before starting new recording
                  setTimeout(() => {
                    startRecording();
                  }, 100);
                }}
                disabled={isSubmitting}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(144, 202, 249, 0.08)'
                  }
                }}
                title="Re-record"
              >
                <RefreshIcon />
              </IconButton>
            </>
          )}
          {isRecording && (
            <Typography variant="body2" color="error">
              Recording...
            </Typography>
          )}

          {isTranscribing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                Transcribing...
              </Typography>
            </Box>
          )}
        </Box>

        {confirmedTranscription && !isRecording && !isTranscribing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Typography 
              variant="body1" 
              color="text.primary"
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                ml: 1,
                opacity: 0.9
              }}
            >
              {confirmedTranscription}
            </Typography>
            <IconButton
              onClick={() => {
                setEditedTranscription(confirmedTranscription);
                setShowTranscriptionDialog(true);
              }}
              size="small"
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'rgba(144, 202, 249, 0.08)'
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Dialog 
        open={showTranscriptionDialog} 
        onClose={() => {
          console.log('[AudioRecorderCore] Dialog closed via onClose');
          setShowTranscriptionDialog(false);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={false}
        disablePortal={false}
        sx={{ zIndex: 1500 }}
      >
        <DialogTitle>Review Transcription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please review and edit the transcription if needed:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={editedTranscription}
            onChange={(e) => setEditedTranscription(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              // Close dialog and clear recording in one step
              document.body.click(); // Force blur on any focused elements
              setShowTranscriptionDialog(false);
              deleteRecording();
            }}
            color="error"
            variant="outlined"
          >
            Re-record
          </Button>
          <Button onClick={handleConfirmTranscription} variant="contained" startIcon={<SaveIcon />}>
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
